import React from "react";
import { ALL_PAGES_SIZE } from "../../../constants/constants";
import { OrganizationProduct } from "../../../domain/OrganizationProduct";
import { GroupTypeFilter } from "./GroupTypeFilter";
import { IGroupFilters } from "../../../types/GroupFilters";
import { useProducts } from "../../../hooks/useProducts/useProducts";
import { useUserContext } from "../../../providers/UserProvider";
import { GenericDropdownFilter } from "./GenericDropdownFilter";
import { useRoles } from "../../../hooks/useRoles/useRoles";

interface GranularGroupFiltersProps {
    className?: string;
    onFiltersChange: (updates: { type: string; value: any }[]) => void;
    selectedFilters: IGroupFilters;
}

export const GranularGroupFilters: React.FC<GranularGroupFiltersProps> = ({
    className = "",
    onFiltersChange,
    selectedFilters
}) => {
    const { organizationId } = useUserContext();
    const { products } = useProducts({
        organizationId
    });
    const { roles, isLoading: rolesLoading } = useRoles({
        organizationId,
        productId: selectedFilters?.product?.id,
        instanceId: selectedFilters?.instance?.id,
        size: ALL_PAGES_SIZE
    });

    return (
        <div className={className}>
            <GroupTypeFilter
                onChange={(groupTypes) => onFiltersChange([{ type: "groupTypes", value: groupTypes }])}
                selectedGroupTypes={selectedFilters?.groupTypes || []}
                hideProductGroupFilter={false}
            />
            <GenericDropdownFilter
                data-cy="product-filter"
                label="Product"
                onChange={({ value }) => {
                    const selectedProduct = value as OrganizationProduct | null;
                    // if there is only one product instance, automatically select it as a filter
                    const filterUpdates: { type: string; value: any }[] = [{ type: "product", value }];
                    if (selectedProduct?.instances?.length === 1) {
                        filterUpdates.push({ type: "instance", value: selectedProduct.instances[0] });
                    } else if (selectedFilters?.instance) {
                        // reset on product change if instance is already selected
                        filterUpdates.push({ type: "instance", value: null });
                    }
                    filterUpdates.push({ type: "role", value: null }); // reset role filter on product change
                    onFiltersChange(filterUpdates);
                }}
                options={products || []}
                value={selectedFilters?.product || null}
            />
            <GenericDropdownFilter
                data-cy="instance-filter"
                disabled={!selectedFilters?.product}
                label="Instances"
                hideAllOption={selectedFilters.product?.instances?.length === 1}
                onChange={({ value }) => {
                    onFiltersChange([{ type: "instance", value }]);
                }}
                options={selectedFilters?.product?.instances || []}
                value={selectedFilters?.instance || null}
            />
            <GenericDropdownFilter
                data-cy="role-filter"
                disabled={rolesLoading || !selectedFilters?.product}
                label="Roles"
                onChange={({ value }) => {
                    onFiltersChange([{ type: "role", value }]);
                }}
                options={roles || []}
                value={selectedFilters?.role || null}
            />
        </div>
    );
};

GranularGroupFilters.displayName = "GranularGroupFilters";
